import { useState, useEffect, useRef } from 'react';

import { BASE_PATH, STATIC_PATH } from 'constants';
import { useDebounce } from 'hooks';

/**
 * This hook uses the performance API to compile and automatically update a list
 * of paths for all static app resources in any environment.
 *
 * It includes the root app path, which can be accessed at
 * "/", "/read", "/read/{epubId}", and "/read/{epubId}/{documentId}"
 * and is represented canonically as "/read"
 *
 * Expectation: all necessary resources for using the reader will load on initial app load.
 *
 * @returns {Array} an array of strings representing static resource URL paths
 */
export default function useStaticResourcePaths() {
  const pathsString = useRef();
  const [paths, setPaths] = useState([]);

  // Debounce updates, as the path list will change often on initial load.
  const pathsDebounced = useDebounce(paths);

  // Watch for new static app resources.
  useEffect(() => {
    const observer = new PerformanceObserver(() => {
      const allPaths = [
        // some paths we have to hard code because the requests come from the iframe or are part of errors for no networks.
        `${STATIC_PATH}/css/readium-before.css`,
        `${STATIC_PATH}/css/readium-after.css`,
        `${STATIC_PATH}/images/owl.svg`,
        `${STATIC_PATH}/fonts/opendyslexic/OpenDyslexic-Regular.woff2`,
        `${STATIC_PATH}/fonts/opendyslexic/OpenDyslexic-Bold.woff2`,
        `${STATIC_PATH}/fonts/opendyslexic/OpenDyslexic-Italic.woff2`,
        `${STATIC_PATH}/fonts/opendyslexic/OpenDyslexic-Bold-Italic.woff2`,
        ...performance
          .getEntriesByType('resource')
          .map((resource) => resource.name.split(window.location.host)[1]),
      ];

      const uniquePaths = allPaths.filter((item, position) => {
        return allPaths.indexOf(item) === position;
      });

      const relevantPaths = [
        BASE_PATH,
        ...uniquePaths
          .filter((url) => url && url.startsWith(STATIC_PATH))
          .filter((url) => url && !url.endsWith('/sw.js')),
      ]; // do not include the service worker

      // Prevent setting the state to a new array object if the path lists are identical.
      const newPathString = JSON.stringify(relevantPaths);
      if (newPathString === pathsString.current) {
        return;
      }
      // Update the staticResourcePaths state.
      pathsString.current = newPathString;
      setPaths(relevantPaths);
    });

    observer.observe({ entryTypes: ['resource'] });

    return () => observer.disconnect();
  }, []);

  return pathsDebounced.value;
}
